import {createApi} from '@reduxjs/toolkit/dist/query/react';
import {axiosBaseQuery} from 'src/axiosBaseQuery';
import {ResetPasswordRequest} from 'src/types/auth';
import {setAuthInfo} from 'src/stores/common/commonSlice';
import {getBaseURL} from 'src/utils/helper';
import {DefaultAuthResponse, DefaultResponse} from 'src/types/common';

export const authApi = createApi({
    reducerPath: 'authApi',
    // No `REFETCH_POLICY` here, unlike every other slice: nothing in this one is data to be kept
    // fresh. Signing in, renewing a session, signing out and resetting a password each happen once,
    // when asked - an automatic re-ask is at best a surprise, and for the renewal below it was a
    // second POST of a live token every time the window regained focus.
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL() + '/auth'),
    }),
    endpoints: (build) => {
        return {
            login: build.mutation<DefaultAuthResponse, { emailAddress: string; password: string }>({
                query: (data) => ({url: '/login', method: 'post', data}),
                async onQueryStarted(_id, {dispatch, queryFulfilled}) {
                    try {
                        const {data} = await queryFulfilled;
                        dispatch(setAuthInfo(data.payload));
                    } catch (err) {
                        console.error(err);
                    }
                },
            }),
            /**
             * Renews the session, and is a mutation rather than a query on purpose - see `runRefresh`
             * in `src/axiosBaseQuery/index.ts` for what being a query cost. In short: a query is
             * cached by its argument and subscribed to, so the refresh token sat in the store as an
             * entry the window's focus event re-POSTed, and a second caller could be handed that
             * cached entry instead of waiting for the renewal it had just asked for.
             */
            refreshToken: build.mutation<DefaultResponse, { token: string }>({
                query: (params) => ({url: `/refresh/token`, method: 'post', params}),
            }),
            // Ends the session on the server too. Clearing localStorage only made this browser forget
            // it; the session stayed open, valid to whoever held the token, until it aged out.
            logout: build.mutation<DefaultResponse, { token: string }>({
                query: (params) => ({url: '/logout', method: 'post', params}),
            }),
            forgotPassword: build.query<DefaultResponse, { emailAddress: string }>({
                query: (data) => ({url: '/password/forget', method: 'post', data}),
            }),
            resetPassword: build.mutation<DefaultResponse, ResetPasswordRequest>({
                query: ({data, params}) => ({url: '/password/reset', method: 'post', data, params}),
            }),
            // An invited company credential choosing its own password. Public, like the reset above:
            // the hash from the invitation mail is the only thing the caller holds, and no session is
            // issued for it - the person signs in afterwards like anybody else.
            setCompanyCredentialPassword: build.mutation<DefaultResponse, ResetPasswordRequest>({
                query: ({data, params}) => ({url: '/company-credentials/password', method: 'post', data, params}),
            }),
        };
    },
});
